if (!customElements.get('product-customization')) {
  class ProductCustomization extends HTMLElement {
    connectedCallback() {
      this.modal = document.getElementById(this.dataset.modalId);
      if (!this.modal) return;

      this.input =
        this.modal.querySelector('[data-customization-input] .field__input') ||
        this.modal.querySelector('[data-customization-input] input');
      this.saveButton = this.modal.querySelector('[data-customization-save]');
      this.clearButton = this.modal.querySelector('[data-customization-clear]');
      this.countElement = this.modal.querySelector('[data-customization-count]');
      this.hintElement = this.modal.querySelector('[data-customization-hint]');
      this.hiddenInput = this.querySelector('[data-customization-property]');
      this.hiddenIconsInput = this.querySelector('[data-customization-icons-property]');
      this.hiddenSizeInput = this.querySelector('[data-customization-size-property]');
      this.hiddenFontInput = this.querySelector('[data-customization-font-property]');
      this.hiddenColorInput = this.querySelector('[data-customization-color-property]');
      this.hiddenApplicationSiteInput = this.querySelector('[data-customization-application-site-property]');
      this.hiddenSequenceInput = this.querySelector('[data-customization-sequence-property]');
      this.iconsRoot = this.modal.querySelector('[data-customization-icons]');
      this.sizesRoot = this.modal.querySelector('[data-customization-sizes]');
      this.fontsRoot = this.modal.querySelector('[data-customization-fonts]');
      this.colorsRoot = this.modal.querySelector('[data-customization-colors]');
      this.sitesRoot = this.modal.querySelector('[data-customization-sites]');
      this.bodyElement = this.modal.querySelector('.product-customization__body');
      this.previewMarking = this.modal.querySelector('[data-preview-marking]');
      this.previewText = null;
      this.previewRoot = this.modal.querySelector('[data-customization-preview]');
      this.embossExample = this.previewRoot?.querySelector('[data-emboss-example]');
      this.previewBgImage = this.previewRoot?.querySelector('[data-preview-bg-image]');
      this.embossExampleImage = this.previewRoot?.querySelector('[data-preview-emboss-image]');
      this.variantVisualization = this.parseVariantVisualization();
      this.currentVariantId = this.getCurrentVariantId();
      this.propertyName = this.dataset.propertyName || 'Personalization';
      this.iconsPropertyName = this.dataset.iconsPropertyName || 'Personalization icons';
      this.sizePropertyName = this.dataset.sizePropertyName || 'Personalization size';
      this.fontPropertyName = this.dataset.fontPropertyName || 'Personalization font';
      this.colorPropertyName = this.dataset.colorPropertyName || 'Personalization color';
      this.applicationSitePropertyName =
        this.dataset.applicationSitePropertyName || 'Personalization application site';
      this.sequencePropertyName = this.dataset.sequencePropertyName || 'Personalization sequence';
      this.markingQueue = [];
      this.committedValue = '';
      this.committedIconIds = [];
      this.committedMarkingQueue = [];
      this.committedSizeId = this.getSelectedSize()?.dataset.sizeId || '';
      this.committedFontId = this.getSelectedFont()?.dataset.fontId || '';
      this.committedColorId = this.getSelectedColor()?.dataset.colorId || '';
      this.committedApplicationSiteId = this.getSelectedApplicationSite()?.dataset.siteId || '';

      this.onInput = this.onInput.bind(this);
      this.onSave = this.onSave.bind(this);
      this.onClear = this.onClear.bind(this);
      this.onIconChange = this.onIconChange.bind(this);
      this.onSizeChange = this.onSizeChange.bind(this);
      this.onFontChange = this.onFontChange.bind(this);
      this.onColorChange = this.onColorChange.bind(this);
      this.onSiteChange = this.onSiteChange.bind(this);
      this.onTooltipOver = this.onTooltipOver.bind(this);
      this.onTooltipOut = this.onTooltipOut.bind(this);
      this.onGroupToggle = this.onGroupToggle.bind(this);
      this.onSectionUnload = this.onSectionUnload.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.input?.addEventListener('input', this.onInput);
      this.saveButton?.addEventListener('click', this.onSave);
      this.clearButton?.addEventListener('click', this.onClear);
      this.iconsRoot?.addEventListener('change', this.onIconChange);
      this.sizesRoot?.addEventListener('change', this.onSizeChange);
      this.fontsRoot?.addEventListener('change', this.onFontChange);
      this.colorsRoot?.addEventListener('change', this.onColorChange);
      this.sitesRoot?.addEventListener('change', this.onSiteChange);
      this.modal.addEventListener('mouseover', this.onTooltipOver);
      this.modal.addEventListener('mouseout', this.onTooltipOut);
      this.modal.addEventListener('focusin', this.onTooltipOver);
      this.modal.addEventListener('focusout', this.onTooltipOut);
      this.modal.addEventListener('click', this.onGroupToggle);

      this.applySelectedSize();
      this.applyFontFamily();
      this.updateCount();
      this.updateSizeState();
      this.updateFontState();
      this.updateIconState();
      this.applyVariantVisualization();
      this.updatePreview();

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);

      this.modalObserver = new MutationObserver(() => {
        if (this.modal.hasAttribute('open')) {
          this.input?.focus();
          return;
        }

        this.restoreCommitted();
        this.resetAllTooltips();
      });
      this.modalObserver.observe(this.modal, {
        attributes: true,
        attributeFilter: ['open'],
      });

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:unload', this.onSectionUnload);
      }
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.onInput);
      this.saveButton?.removeEventListener('click', this.onSave);
      this.clearButton?.removeEventListener('click', this.onClear);
      this.iconsRoot?.removeEventListener('change', this.onIconChange);
      this.sizesRoot?.removeEventListener('change', this.onSizeChange);
      this.fontsRoot?.removeEventListener('change', this.onFontChange);
      this.colorsRoot?.removeEventListener('change', this.onColorChange);
      this.sitesRoot?.removeEventListener('change', this.onSiteChange);
      this.modal?.removeEventListener('mouseover', this.onTooltipOver);
      this.modal?.removeEventListener('mouseout', this.onTooltipOut);
      this.modal?.removeEventListener('focusin', this.onTooltipOver);
      this.modal?.removeEventListener('focusout', this.onTooltipOut);
      this.modal?.removeEventListener('click', this.onGroupToggle);
      this.modalObserver?.disconnect();
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = null;
      }
    }

    parseMaxCharacters(value) {
      const maxCharacters = Number(value);
      return Number.isFinite(maxCharacters) && maxCharacters > 0 ? maxCharacters : Number.POSITIVE_INFINITY;
    }

    onInput(event) {
      const input = event.target;
      const selectionStart = input.selectionStart;
      const previousValue = input.value;
      const nextValue = this.sanitize(previousValue);

      if (previousValue !== nextValue) {
        input.value = nextValue;
        const cursor = Math.max(0, selectionStart - (previousValue.length - nextValue.length));
        input.setSelectionRange(cursor, cursor);
      }

      this.updateCount();
      this.updateSizeState();
      this.updateFontState();
      this.updatePreview();
    }

    onIconChange() {
      this.updateIconState();
      this.updatePreview();
    }

    onColorChange() {
      this.updatePreview();
    }

    onSiteChange() {
      this.updatePreview();
    }

    onSizeChange() {
      this.applySelectedSize();
      if (this.input) {
        this.input.value = this.sanitize(this.input.value);
      }
      this.updateCount();
      this.updateSizeState();
      this.updateFontState();
      this.updatePreview();
    }

    onFontChange() {
      const previousSizeId = this.getSelectedSize()?.dataset.sizeId || '';
      this.showSizesForFont(this.getSelectedFont()?.dataset.fontId || '');
      this.selectSizeForFont(previousSizeId);
      this.applySelectedSize();
      this.applyFontFamily();
      if (this.input) {
        this.input.value = this.sanitize(this.input.value);
      }
      this.updateCount();
      this.updateSizeState();
      this.updateFontState();
      this.updatePreview();
    }

    onSave() {
      this.syncMarkingQueue();
      this.committedValue = this.sanitize(this.input?.value || '').trimEnd();
      this.committedIconIds = this.getSelectedIconIds();
      this.committedMarkingQueue = this.copyMarkingQueue(this.markingQueue);
      this.committedSizeId = this.getSelectedSize()?.dataset.sizeId || '';
      this.committedFontId = this.getSelectedFont()?.dataset.fontId || '';
      this.committedColorId = this.getSelectedColor()?.dataset.colorId || '';
      this.committedApplicationSiteId = this.getSelectedApplicationSite()?.dataset.siteId || '';
      this.syncHiddenInput();
      this.syncHiddenIconsInput();
      this.syncHiddenSequenceInput();
      this.syncHiddenSizeInput();
      this.syncHiddenFontInput();
      this.syncHiddenColorInput();
      this.syncHiddenApplicationSiteInput();
      this.modal.hide();
    }

    onClear() {
      if (this.input) {
        this.input.value = '';
      }

      this.getIconCheckboxes().forEach((checkbox) => {
        checkbox.checked = false;
      });

      this.selectFirstFont();
      this.selectSmallestSize();
      this.selectFirstApplicationSite();
      this.selectFirstColor();
      this.markingQueue = [];
      this.applySelectedSize();
      this.applyFontFamily();
      this.updateCount();
      this.updateSizeState();
      this.updateFontState();
      this.updateIconState();
      this.updatePreview();
      this.input?.focus();
    }

    restoreCommitted() {
      if (this.input) {
        this.input.value = this.committedValue;
      }

      const committedIds = new Set(this.committedIconIds);
      this.getIconCheckboxes().forEach((checkbox) => {
        checkbox.checked = committedIds.has(checkbox.dataset.iconId);
      });

      this.selectFontById(this.committedFontId);
      this.selectSizeById(this.committedSizeId);
      this.selectApplicationSiteById(this.committedApplicationSiteId);
      this.selectColorById(this.committedColorId);
      this.markingQueue = this.copyMarkingQueue(this.committedMarkingQueue);
      this.applySelectedSize();
      this.applyFontFamily();
      this.updateCount();
      this.updateSizeState();
      this.updateFontState();
      this.updateIconState();
      this.updatePreview();
    }

    updatePreview() {
      this.syncMarkingQueue();
      this.applyPreviewTexture();
      this.applyPreviewPosition();

      if (this.previewRoot?.classList.contains('product-customization__preview--example')) {
        if (this.previewMarking) {
          this.previewMarking.replaceChildren();
          this.previewText = null;
        }
        return;
      }

      this.renderPreviewMarking();
      this.applyPreviewTextSize();
      this.applyFontFamily();
    }

    copyMarkingQueue(queue) {
      return (queue || []).map((item) => ({ ...item }));
    }

    syncMarkingQueue() {
      const text = this.input?.value || '';
      const selectedIds = new Set(
        Array.from(this.getIconCheckboxes())
          .filter((checkbox) => checkbox.checked)
          .map((checkbox) => checkbox.dataset.iconId)
      );

      this.markingQueue = this.markingQueue.filter((item) => {
        if (item.type === 'text') return text.length > 0;
        if (item.type === 'icon') return selectedIds.has(item.id);
        return false;
      });

      if (text.length > 0 && !this.markingQueue.some((item) => item.type === 'text')) {
        this.markingQueue.push({ type: 'text' });
      }

      selectedIds.forEach((id) => {
        if (!this.markingQueue.some((item) => item.type === 'icon' && item.id === id)) {
          this.markingQueue.push({ type: 'icon', id });
        }
      });
    }

    renderPreviewMarking() {
      if (!this.previewMarking) return;

      const nodes = this.markingQueue
        .map((item) => {
          if (item.type === 'text') {
            const text = document.createElement('span');
            text.className = 'product-customization__preview-text';
            text.dataset.previewText = '';
            text.textContent = this.input?.value || '';
            return text;
          }

          const image = this.getIconCheckboxById(item.id)?.closest('label')?.querySelector('img');
          return image ? this.createPreviewIcon(image) : null;
        })
        .filter(Boolean);

      this.previewMarking.replaceChildren(...nodes);
      this.previewText = this.previewMarking.querySelector('[data-preview-text]');
    }

    parseVariantVisualization() {
      const script = this.querySelector('[data-variant-visualization]');
      if (!script) return {};

      try {
        return JSON.parse(script.textContent);
      } catch {
        return {};
      }
    }

    getCurrentVariantId() {
      if (this.dataset.variantId) return this.dataset.variantId;

      const formId = this.hiddenInput?.getAttribute('form');
      const form = formId ? document.getElementById(formId) : null;
      return form?.querySelector('input[name="id"]')?.value || '';
    }

    getVariantMedia(variantId) {
      if (variantId == null || variantId === '') return null;
      return this.variantVisualization[variantId] || this.variantVisualization[String(variantId)] || null;
    }

    onVariantChange(event) {
      if (String(event.data?.sectionId) !== String(this.dataset.sectionId)) return;

      const variantId = event.data?.variant?.id;
      if (!variantId) return;

      this.currentVariantId = String(variantId);
      this.dataset.variantId = this.currentVariantId;
      this.applyVariantVisualization();
      this.updatePreview();
    }

    applyVariantVisualization() {
      const media = this.getVariantMedia(this.currentVariantId);
      if (!media) return;

      this.setPreviewImage(this.previewBgImage, media.background);
      this.setPreviewImage(this.embossExampleImage, media.emboss);
    }

    setPreviewImage(image, url) {
      if (!image) return;

      if (url) {
        image.src = url;
        image.srcset = url;
        image.hidden = false;
        return;
      }

      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.hidden = true;
    }

    applyPreviewTexture() {
      if (!this.previewRoot) return;

      const selectedColor = this.getSelectedColor();
      const media = this.getVariantMedia(this.currentVariantId);
      const hasEmboss = media ? Boolean(media.emboss) : Boolean(this.embossExample);
      const showExample =
        selectedColor?.dataset.blindEmboss === 'true' && Boolean(this.embossExample) && hasEmboss;

      this.previewRoot.classList.toggle('product-customization__preview--example', showExample);
      if (this.embossExample) {
        this.embossExample.hidden = !showExample;
      }

      if (showExample) {
        this.previewRoot.style.removeProperty('--preview-texture');
        this.previewRoot.classList.remove('product-customization__preview--textured');
        return;
      }

      const textureUrl = selectedColor?.dataset.colorImage || '';

      if (textureUrl) {
        this.previewRoot.style.setProperty('--preview-texture', `url(${JSON.stringify(textureUrl)})`);
        this.previewRoot.classList.add('product-customization__preview--textured');
        return;
      }

      this.previewRoot.style.removeProperty('--preview-texture');
      this.previewRoot.classList.remove('product-customization__preview--textured');
    }

    applyPreviewPosition() {
      if (!this.previewMarking) return;

      const siteId = this.getSelectedApplicationSite()?.dataset.siteId || '';
      const position = siteId === 'top' || siteId === 'bottom' ? siteId : 'middle';
      this.previewMarking.dataset.position = position;
    }

    applyPreviewTextSize() {
      if (!this.previewText) return;

      const sizeMm = Number(this.getSelectedSize()?.dataset.sizeMm);
      const pxPerMm = Number(this.dataset.pxPerMm) || 4;

      if (Number.isFinite(sizeMm) && sizeMm > 0) {
        this.previewText.style.fontSize = `${sizeMm * pxPerMm}px`;
        return;
      }

      this.previewText.style.removeProperty('font-size');
    }

    createPreviewIcon(image) {
      const icon = document.createElement('span');
      icon.className = 'product-customization__preview-icon';
      icon.setAttribute('aria-hidden', 'true');

      const maskUrl = image.currentSrc || image.src;
      if (maskUrl) {
        icon.style.setProperty('--preview-icon-mask', `url(${JSON.stringify(maskUrl)})`);
      }

      if (image.naturalWidth && image.naturalHeight) {
        icon.style.aspectRatio = `${image.naturalWidth} / ${image.naturalHeight}`;
      }

      return icon;
    }

    applySelectedSize() {
      const selectedSize = this.getSelectedSize();
      if (selectedSize) {
        this.maxCharacters = this.parseMaxCharacters(selectedSize.dataset.maxCharacters);
        return;
      }

      this.maxCharacters = this.parseMaxCharacters(this.dataset.maxCharacters);
    }

    selectSmallestSize() {
      const radios = this.getSizeRadios();
      if (!radios.length) return;

      radios[0].checked = true;
    }

    selectSizeById(sizeId) {
      const radios = this.getSizeRadios();
      if (!radios.length) return;

      const match = Array.from(radios).find((radio) => radio.dataset.sizeId === sizeId);
      if (match) {
        match.checked = true;
        return;
      }

      this.selectSmallestSize();
    }

    getSizeRadios() {
      const group = this.getActiveSizeGroup();
      return group ? group.querySelectorAll('[data-size-radio]') : [];
    }

    getSelectedSize() {
      return this.getActiveSizeGroup()?.querySelector('[data-size-radio]:checked') || null;
    }

    getActiveSizeGroup() {
      return this.sizesRoot?.querySelector('[data-font-sizes]:not([hidden])') || null;
    }

    showSizesForFont(fontId) {
      if (!this.sizesRoot) return;

      this.sizesRoot.querySelectorAll('[data-font-sizes]').forEach((group) => {
        group.hidden = group.dataset.fontId !== fontId;
      });
    }

    selectSizeForFont(preferredSizeId) {
      const radios = Array.from(this.getSizeRadios());
      if (!radios.length) return;

      const currentLength = this.input?.value.length || 0;
      const preferred = radios.find((radio) => radio.dataset.sizeId === preferredSizeId);
      if (preferred && this.parseMaxCharacters(preferred.dataset.maxCharacters) >= currentLength) {
        preferred.checked = true;
        return;
      }

      const fitting = radios.find(
        (radio) => this.parseMaxCharacters(radio.dataset.maxCharacters) >= currentLength
      );
      (fitting || radios[0]).checked = true;
    }

    applyFontFamily() {
      this.input?.style.removeProperty('font-family');

      const selectedFont = this.getSelectedFont();
      if (this.previewRoot) {
        this.previewRoot.dataset.previewFont = selectedFont?.dataset.fontId || '';
      }

      if (this.previewText) {
        this.previewText.style.fontFamily = selectedFont?.dataset.fontFamily || '';
      }
    }

    selectFirstFont() {
      const radios = this.getFontRadios();
      if (!radios.length) return;

      radios[0].checked = true;
      this.showSizesForFont(radios[0].dataset.fontId || '');
    }

    selectFontById(fontId) {
      const radios = this.getFontRadios();
      if (!radios.length) return;

      const match = Array.from(radios).find((radio) => radio.dataset.fontId === fontId);
      if (match) {
        match.checked = true;
        this.showSizesForFont(match.dataset.fontId || '');
        return;
      }

      this.selectFirstFont();
    }

    getFontRadios() {
      return this.fontsRoot ? this.fontsRoot.querySelectorAll('[data-font-radio]') : [];
    }

    getSelectedFont() {
      return this.fontsRoot?.querySelector('[data-font-radio]:checked') || null;
    }

    updateFontState() {
      if (!this.fontsRoot) return;

      const currentLength = this.input?.value.length || 0;
      const template = this.fontsRoot.dataset.disabledFontTemplate || '';

      this.getFontRadios().forEach((radio) => {
        const maxCharacters = this.parseMaxCharacters(radio.dataset.maxCharacters);
        const isSelected = radio.checked;
        const tooLong = Number.isFinite(maxCharacters) && currentLength > maxCharacters;
        const disabled = tooLong && !isSelected;
        const reason = disabled
          ? template.replace('__max__', String(maxCharacters)).replace('__current__', String(currentLength))
          : '';

        this.setFontDisabled(radio, disabled, reason);
      });
    }

    setFontDisabled(radio, disabled, reason) {
      radio.disabled = disabled;
      const label = radio.closest('[data-font-label]');
      if (!label) return;

      const tooltip = label.querySelector('[data-font-tooltip]');

      if (disabled && reason) {
        label.setAttribute('data-disabled-reason', reason);
        label.setAttribute('title', reason);
        if (tooltip) {
          tooltip.hidden = false;
          tooltip.textContent = reason;
        }
        return;
      }

      label.removeAttribute('data-disabled-reason');
      label.removeAttribute('title');
      if (tooltip) {
        tooltip.hidden = true;
        tooltip.textContent = '';
      }
    }

    selectFirstColor() {
      const radios = this.getColorRadios();
      if (!radios.length) return;

      radios[0].checked = true;
    }

    selectColorById(colorId) {
      const radios = this.getColorRadios();
      if (!radios.length) return;

      const match = Array.from(radios).find((radio) => radio.dataset.colorId === colorId);
      if (match) {
        match.checked = true;
        return;
      }

      this.selectFirstColor();
    }

    getColorRadios() {
      return this.colorsRoot ? this.colorsRoot.querySelectorAll('[data-color-radio]') : [];
    }

    getSelectedColor() {
      return this.colorsRoot?.querySelector('[data-color-radio]:checked') || null;
    }

    selectFirstApplicationSite() {
      const radios = this.getApplicationSiteRadios();
      if (!radios.length) return;

      radios[0].checked = true;
    }

    selectApplicationSiteById(siteId) {
      const radios = this.getApplicationSiteRadios();
      if (!radios.length) return;

      const match = Array.from(radios).find((radio) => radio.dataset.siteId === siteId);
      if (match) {
        match.checked = true;
        return;
      }

      this.selectFirstApplicationSite();
    }

    getApplicationSiteRadios() {
      return this.sitesRoot ? this.sitesRoot.querySelectorAll('[data-site-radio]') : [];
    }

    getSelectedApplicationSite() {
      return this.sitesRoot?.querySelector('[data-site-radio]:checked') || null;
    }

    updateSizeState() {
      if (!this.sizesRoot) return;

      const currentLength = this.input?.value.length || 0;
      const template = this.sizesRoot.dataset.disabledSizeTemplate || '';

      this.getSizeRadios().forEach((radio) => {
        const maxCharacters = this.parseMaxCharacters(radio.dataset.maxCharacters);
        const isSelected = radio.checked;
        const tooLong = Number.isFinite(maxCharacters) && currentLength > maxCharacters;
        const disabled = tooLong && !isSelected;
        const reason = disabled
          ? template.replace('__max__', String(maxCharacters)).replace('__current__', String(currentLength))
          : '';

        this.setSizeDisabled(radio, disabled, reason);
      });
    }

    setSizeDisabled(radio, disabled, reason) {
      radio.disabled = disabled;
      const label = radio.closest('[data-size-label]');
      if (!label) return;

      const tooltip = label.querySelector('[data-size-tooltip]');

      if (disabled && reason) {
        label.setAttribute('data-disabled-reason', reason);
        label.setAttribute('title', reason);
        if (tooltip) {
          tooltip.hidden = false;
          tooltip.textContent = reason;
        }
        return;
      }

      label.removeAttribute('data-disabled-reason');
      label.removeAttribute('title');
      if (tooltip) {
        tooltip.hidden = true;
        tooltip.textContent = '';
      }
    }

    updateCount() {
      const current = this.input?.value.length || 0;
      const maxLabel = Number.isFinite(this.maxCharacters) ? String(this.maxCharacters) : '—';

      if (this.countElement) {
        const template = this.countElement.dataset.countTemplate || '{{ current }} / {{ max }}';
        this.countElement.textContent = template
          .replace('__current__', String(current))
          .replace('__max__', maxLabel);
      }

      if (this.hintElement) {
        const template = this.hintElement.dataset.hintTemplate || '';
        if (template) {
          this.hintElement.textContent = template.replace('__count__', maxLabel);
        }
      }
    }

    getIconCheckboxes() {
      return this.modal.querySelectorAll('[data-icon-checkbox]');
    }

    getIconCheckboxById(iconId) {
      return Array.from(this.getIconCheckboxes()).find((checkbox) => checkbox.dataset.iconId === iconId) || null;
    }

    getSelectedIconIds() {
      return this.markingQueue.filter((item) => item.type === 'icon').map((item) => item.id);
    }

    getSelectedIconTitles() {
      return this.getSelectedIconIds()
        .map((iconId) => this.getIconCheckboxById(iconId)?.value || '')
        .filter(Boolean);
    }

    updateIconState() {
      if (!this.iconsRoot) return;

      const categories = this.iconsRoot.querySelectorAll('[data-icon-category]');
      const otherReason = this.iconsRoot.dataset.disabledOther || '';
      const maxTemplate = this.iconsRoot.dataset.disabledMaxTemplate || '';
      const countTemplate = this.iconsRoot.dataset.categoryCountTemplate || '(__count__)';
      let activeCategory = null;

      categories.forEach((category) => {
        const selectedCount = category.querySelectorAll('[data-icon-checkbox]:checked').length;
        this.updateCategoryCount(category, selectedCount, countTemplate);
        if (selectedCount > 0) {
          activeCategory = category;
        }
      });

      categories.forEach((category) => {
        const maxCount = Number(category.dataset.maxCount) || 1;
        const selectedCount = category.querySelectorAll('[data-icon-checkbox]:checked').length;
        const otherCategorySelected = Boolean(activeCategory && activeCategory !== category);
        const atMax = selectedCount >= maxCount;
        const maxReason = maxTemplate.replace('__count__', String(maxCount));

        category.querySelectorAll('[data-icon-checkbox]').forEach((checkbox) => {
          let disabled = false;
          let reason = '';

          if (!checkbox.checked && otherCategorySelected) {
            disabled = true;
            reason = otherReason;
          } else if (!checkbox.checked && atMax) {
            disabled = true;
            reason = maxReason;
          }

          this.setIconDisabled(checkbox, disabled, reason);
        });
      });
    }

    updateCategoryCount(category, selectedCount, countTemplate) {
      const countElement = category.querySelector('[data-category-count]');
      if (!countElement) return;

      if (selectedCount > 0) {
        countElement.hidden = false;
        countElement.textContent = countTemplate.replace('__count__', String(selectedCount));
        return;
      }

      countElement.hidden = true;
      countElement.textContent = '';
    }

    setIconDisabled(checkbox, disabled, reason) {
      checkbox.disabled = disabled;
      const label = checkbox.closest('[data-icon-label]');
      if (!label) return;

      const tooltip = label.querySelector('[data-icon-tooltip]');

      if (disabled && reason) {
        label.setAttribute('data-disabled-reason', reason);
        label.setAttribute('title', reason);
        if (tooltip) {
          tooltip.hidden = false;
          tooltip.textContent = reason;
        }
        return;
      }

      label.removeAttribute('data-disabled-reason');
      label.removeAttribute('title');
      if (tooltip) {
        tooltip.hidden = true;
        tooltip.textContent = '';
      }
    }

    syncHiddenInput() {
      if (!this.hiddenInput) return;

      if (this.committedValue) {
        this.hiddenInput.name = `properties[${this.propertyName}]`;
        this.hiddenInput.value = this.committedValue;
        this.hiddenInput.disabled = false;
        return;
      }

      this.hiddenInput.removeAttribute('name');
      this.hiddenInput.value = '';
      this.hiddenInput.disabled = true;
    }

    syncHiddenIconsInput() {
      if (!this.hiddenIconsInput) return;

      const titles = this.getSelectedIconTitles();

      if (titles.length > 0) {
        this.hiddenIconsInput.name = `properties[${this.iconsPropertyName}]`;
        this.hiddenIconsInput.value = titles.join(', ');
        this.hiddenIconsInput.disabled = false;
        return;
      }

      this.hiddenIconsInput.removeAttribute('name');
      this.hiddenIconsInput.value = '';
      this.hiddenIconsInput.disabled = true;
    }

    syncHiddenSequenceInput() {
      if (!this.hiddenSequenceInput) return;

      const labels = this.committedMarkingQueue
        .map((item) => {
          if (item.type === 'text') return this.committedValue;
          return this.getIconCheckboxById(item.id)?.value || '';
        })
        .filter(Boolean);

      if (labels.length > 0) {
        this.hiddenSequenceInput.name = `properties[${this.sequencePropertyName}]`;
        this.hiddenSequenceInput.value = labels.join(' | ');
        this.hiddenSequenceInput.disabled = false;
        return;
      }

      this.hiddenSequenceInput.removeAttribute('name');
      this.hiddenSequenceInput.value = '';
      this.hiddenSequenceInput.disabled = true;
    }

    syncHiddenSizeInput() {
      if (!this.hiddenSizeInput) return;

      const selectedSize = this.getSelectedSize();

      if (selectedSize) {
        this.hiddenSizeInput.name = `properties[${this.sizePropertyName}]`;
        this.hiddenSizeInput.value = selectedSize.value;
        this.hiddenSizeInput.disabled = false;
        return;
      }

      this.hiddenSizeInput.removeAttribute('name');
      this.hiddenSizeInput.value = '';
      this.hiddenSizeInput.disabled = true;
    }

    syncHiddenFontInput() {
      if (!this.hiddenFontInput) return;

      const selectedFont = this.getSelectedFont();

      if (selectedFont) {
        this.hiddenFontInput.name = `properties[${this.fontPropertyName}]`;
        this.hiddenFontInput.value = selectedFont.value;
        this.hiddenFontInput.disabled = false;
        return;
      }

      this.hiddenFontInput.removeAttribute('name');
      this.hiddenFontInput.value = '';
      this.hiddenFontInput.disabled = true;
    }

    syncHiddenColorInput() {
      if (!this.hiddenColorInput) return;

      const selectedColor = this.getSelectedColor();

      if (selectedColor) {
        this.hiddenColorInput.name = `properties[${this.colorPropertyName}]`;
        this.hiddenColorInput.value = selectedColor.value;
        this.hiddenColorInput.disabled = false;
        return;
      }

      this.hiddenColorInput.removeAttribute('name');
      this.hiddenColorInput.value = '';
      this.hiddenColorInput.disabled = true;
    }

    syncHiddenApplicationSiteInput() {
      if (!this.hiddenApplicationSiteInput) return;

      const selectedSite = this.getSelectedApplicationSite();

      if (selectedSite) {
        this.hiddenApplicationSiteInput.name = `properties[${this.applicationSitePropertyName}]`;
        this.hiddenApplicationSiteInput.value = selectedSite.value;
        this.hiddenApplicationSiteInput.disabled = false;
        return;
      }

      this.hiddenApplicationSiteInput.removeAttribute('name');
      this.hiddenApplicationSiteInput.value = '';
      this.hiddenApplicationSiteInput.disabled = true;
    }

    sanitize(value) {
      let result = '';

      for (const character of value.toUpperCase()) {
        if (Number.isFinite(this.maxCharacters) && result.length >= this.maxCharacters) break;

        if (/[A-Z0-9.•]/.test(character)) {
          result += character;
          continue;
        }

        if (character === ' ' && result.length > 0 && result[result.length - 1] !== ' ') {
          result += ' ';
        }
      }

      return result;
    }

    onSectionUnload(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;
      this.modal?.remove();
    }

    getTooltipLabel(target) {
      return target?.closest?.('[data-size-label], [data-font-label], [data-color-label], [data-icon-label]') || null;
    }

    onTooltipOver(event) {
      const label = this.getTooltipLabel(event.target);
      if (!label || !this.modal.contains(label)) return;

      const related = event.relatedTarget;
      if (related && label.contains(related)) return;

      const tooltip = label.querySelector('.product-customization__icon-tooltip');
      if (!tooltip || tooltip.hidden) return;

      this.fitTooltip(tooltip);
    }

    onTooltipOut(event) {
      const label = this.getTooltipLabel(event.target);
      if (!label || !this.modal.contains(label)) return;

      const related = event.relatedTarget;
      if (related && label.contains(related)) return;

      this.resetTooltip(label.querySelector('.product-customization__icon-tooltip'));
    }

    fitTooltip(tooltip) {
      this.resetTooltip(tooltip);
      if (!this.bodyElement) return;

      const containerRect = this.bodyElement.getBoundingClientRect();
      let tooltipRect = tooltip.getBoundingClientRect();

      if (tooltipRect.top < containerRect.top) {
        tooltip.style.insetBlockEnd = 'auto';
        tooltip.style.insetBlockStart = 'calc(100% + 0.8rem)';
        tooltipRect = tooltip.getBoundingClientRect();
      }

      let shift = 0;
      if (tooltipRect.left < containerRect.left) {
        shift = containerRect.left - tooltipRect.left;
      } else if (tooltipRect.right > containerRect.right) {
        shift = containerRect.right - tooltipRect.right;
      }

      if (shift !== 0) {
        tooltip.style.transform = `translateX(calc(-50% + ${shift}px))`;
      }
    }

    resetTooltip(tooltip) {
      if (!tooltip) return;

      tooltip.style.insetBlockEnd = '';
      tooltip.style.insetBlockStart = '';
      tooltip.style.transform = '';
    }

    resetAllTooltips() {
      this.modal?.querySelectorAll('.product-customization__icon-tooltip').forEach((tooltip) => {
        this.resetTooltip(tooltip);
      });
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    onGroupToggle(event) {
      const summary = event.target.closest('summary');
      if (!summary || !this.modal.contains(summary)) return;

      const details = summary.closest('details');
      if (
        !details ||
        (!details.classList.contains('product-customization__group') &&
          !details.classList.contains('product-customization__icon-category'))
      ) {
        return;
      }

      event.preventDefault();
      this.animateDetails(details);
    }

    animateDetails(details) {
      const body = details.querySelector(':scope > .product-customization__group-body');
      if (!body) {
        details.open = !details.open;
        return;
      }

      if (details.dataset.animating === 'true') return;

      if (this.prefersReducedMotion()) {
        details.open = !details.open;
        body.style.height = '';
        body.style.overflow = '';
        return;
      }

      if (details.open) {
        this.collapseDetails(details, body);
        return;
      }

      this.expandDetails(details, body);
    }

    expandDetails(details, body) {
      details.dataset.animating = 'true';
      details.open = true;
      const fullHeight = body.scrollHeight;
      body.style.overflow = 'hidden';
      body.style.height = '0px';
      body.offsetHeight;
      body.style.height = `${fullHeight}px`;
      this.onDetailsTransitionEnd(details, body, false);
    }

    collapseDetails(details, body) {
      details.dataset.animating = 'true';
      body.style.overflow = 'hidden';
      body.style.height = `${body.scrollHeight}px`;
      body.offsetHeight;
      body.style.height = '0px';
      this.onDetailsTransitionEnd(details, body, true);
    }

    onDetailsTransitionEnd(details, body, collapse) {
      const finish = (event) => {
        if (event instanceof Event && event.target !== body) return;
        if (event instanceof Event && event.propertyName && event.propertyName !== 'height') return;
        if (details.dataset.animating !== 'true') return;

        body.removeEventListener('transitionend', finish);
        window.clearTimeout(body._customizationToggleTimer);

        if (collapse) {
          details.open = false;
        }

        body.style.height = '';
        body.style.overflow = '';
        delete details.dataset.animating;
      };

      body.addEventListener('transitionend', finish);
      const duration = this.getTransitionMs(body);
      body._customizationToggleTimer = window.setTimeout(() => finish(), duration + 50);
    }

    getTransitionMs(element) {
      const raw = window.getComputedStyle(element).transitionDuration;
      const duration = Number.parseFloat(raw);
      if (!Number.isFinite(duration) || duration <= 0) return 0;
      return raw.includes('ms') ? duration : duration * 1000;
    }
  }

  customElements.define('product-customization', ProductCustomization);
}
